import React, { createContext, useState, useEffect } from 'react';
import {
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  from,
  gql,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { createUploadLink } from 'apollo-upload-client';

import secrets from '../secrets';
import storage from '../utils/storage';
import { sendError } from '../components/ErrorBoundary';

const OWNER_PORTAL_USER_KEY = 'X-Owner-Portal-User-Id';
const REMEMBER_ME_KEY = 'remember-me';

const CURRENT_USER = gql`
  query($ownerType: String) {
    currentUser {
      firstName
      lastName
      fullName
      email
      mobilePhone { value, formatted }
      officePhone { value, formatted }
      ownerPortalUsers(ownerType: $ownerType) {
        id
        role
        ownerType
        ownerName
        ownerId
        ownerAirportCode
      }
    }
  }
`;

const CREATE_PORTAL_ACCESS_LOGIN = gql`
  mutation CreatePortalAccessLogin(
    $ownerPortalUserId: ID!
  ) {
    createPortalAccessLogin(
      ownerPortalUserId: $ownerPortalUserId
    ) {
      ownerPortalUserId
    }
  }
`;

export const AuthContext = createContext();

const setupApolloClient = options => {
  const { clearCredentials, user, ...uploadOptions } = options;

  const uploadLink = createUploadLink(uploadOptions);

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (networkError) {
      if (401 === networkError.statusCode) {
        clearCredentials();
      }
    } else if (graphQLErrors && graphQLErrors[0]) {
      if (graphQLErrors[0].extensions?.code !== 'USER_ERROR') {
        sendError({
          error: {
            name: 'GraphQL Error',
            message: graphQLErrors[0].message,
            stack: JSON.stringify(graphQLErrors),
          },
          user
        });
      }
    }
  });

  const checkHeadersLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(data => {
      const context = operation.getContext();

      if (context.response?.headers) {
        localStorage.setItem(
          'X-User-Log-Id',
          context.response.headers.get('X-User-Log-Id')
        );
      }

      return data;
    });
  });

  return new ApolloClient({
    link: from([checkHeadersLink, errorLink, uploadLink]),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
      query: {
        fetchPolicy: 'no-cache',
      }
    }
  });
};

const ownerPortalUsers = data => {
  if (data) {
    return data.currentUser.ownerPortalUsers
      .filter(ownerPortalUser => 'Fbo' === ownerPortalUser.ownerType);
  } else {
    return [];
  }
};

export const AuthProvider = ({ children }) => {
  const [credentials, setCredentials] = useState();
  const [data, setData] = useState();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [ownerPortalUserId, setOwnerPortalUserId] = useState();
  const [rememberMe, setRememberMe] = useState();
  const [loginAttempted, setLoginAttempted] = useState(false);

  const updateCredentials = values => {
    storage.save({ key: 'credentials', data: values }).then(() => {
      if (credentials?.accessToken !== values?.accessToken) {
        setCredentials(values);
      }
    });
  };

  const updateOwnerPortalUserIdWithoutLog = id => {
    storage.save({ key: OWNER_PORTAL_USER_KEY, data: { id } }).then(() => {
      if (ownerPortalUserId !== id) {
        setOwnerPortalUserId(id);
      }
    });
  };

  const updateRememberMe = email => {
    storage.save({ key: REMEMBER_ME_KEY, data: { email } }).then(() => {
      if (rememberMe !== email) {
        setRememberMe(email);
      }
    });
  };

  const clearCredentials = () => {
    updateOwnerPortalUserIdWithoutLog(null);
    updateCredentials(null);
    setData(null);
    setCredentials(null);
  };

  const updateFromStorage = () => {
    storage.load({ key: 'credentials' }).then(values => {
      setCredentials(values)

      if (values?.accessToken) {
        storage.load({ key: OWNER_PORTAL_USER_KEY }).then(values => {
          setOwnerPortalUserId(values?.id)
        });
      }
    });

    storage.load({ key: REMEMBER_ME_KEY }).then(values => {
      setRememberMe(values?.email);
    });
  };

  const isLoggedIn = !!credentials?.accessToken;

  useEffect(updateFromStorage, []);

  const apolloClientForAuth = setupApolloClient({
    uri: secrets.api.baseUri + '/portal_auth',
    clearCredentials,
  });

  let apolloClient = null;

  const headers = isLoggedIn ? {
    ...credentials,
    'access-token': credentials.accessToken,
    [OWNER_PORTAL_USER_KEY]: (ownerPortalUserId || ''),
  } : {};

  if (isLoggedIn) {
    apolloClient = setupApolloClient({
      uri: secrets.api.baseUri + '/portal_graphql',
      user: data?.user,
      headers,
      clearCredentials,
    });
  }

  const updateOwnerPortalUserId = id => {
    updateOwnerPortalUserIdWithoutLog(id);

    if (apolloClient && id) {
      apolloClient.mutate({
        mutation: CREATE_PORTAL_ACCESS_LOGIN,
        variables: { ownerPortalUserId: id }
      });
    }
  };

  const refetchData = () => {
    if (data) { setData(null) };

    apolloClient.query({ query: CURRENT_USER, variables: {ownerType: 'Fbo'}}).then(response => {
      setDataLoaded(true);
      setData(response.data);
      setLoginAttempted(true);
    });
  };

  if (!data && isLoggedIn) {
    refetchData();
  }

  if (isLoggedIn && !ownerPortalUserId && 1 === ownerPortalUsers(data).length) {
    updateOwnerPortalUserId(ownerPortalUsers(data)[0].id);
  }

  const currentOwnerPortalUser =
    ownerPortalUsers(data).find(ownerPortalUser => (
      ownerPortalUser.id === ownerPortalUserId
    ));

  const value = {
    user: data?.currentUser,
    fboName: currentOwnerPortalUser?.ownerName,
    airportCode: currentOwnerPortalUser?.ownerAirportCode,
    role: currentOwnerPortalUser?.role,
    ownerPortalUsers: ownerPortalUsers(data),
    ownerPortalUsersCount: data?.currentUser?.ownerPortalUsers?.length || 0,
    ownerPortalUserId,
    updateOwnerPortalUserId,
    apolloClient,
    apolloClientForAuth,
    isLoggedIn,
    credentials,
    headers,
    updateCredentials,
    clearCredentials,
    refetchData,
    rememberMe,
    updateRememberMe,
    loginAttempted,
    setLoginAttempted,
    dataLoaded,
    setDataLoaded
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
